<!-- Hero Section with AI/Cloud Theme -->
<section class="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-slate-900">
  <!-- Animated Background Elements -->
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-slate-900 via-slate-900 to-blue-900/20"></div>
    <div class="absolute top-[-10%] right-[-5%] w-96 h-96 bg-blue-600/20 rounded-full blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <!-- Grid Pattern Overlay -->
  <div class="absolute inset-0 z-0 opacity-[0.03]">
    <div class="absolute inset-0" style="background-image: radial-gradient(circle at 10% 20%, rgba(255,255,255,0.1) 1px, transparent 1px); background-size: 30px 30px;"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-900/30 border border-blue-500/30 text-blue-300 mb-6 backdrop-blur-sm">
          <i class="bx bx-rocket text-lg"></i>
          <span class="text-sm font-semibold tracking-wide uppercase">AI-Powered Cloud Infrastructure</span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Next-Gen</span> Cloud Solutions<br>
          <span class="text-white">Powered by</span> <span class="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">AI & GPU</span>
        </h1>
        
        <p class="text-lg text-slate-300 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
          Deploy scalable cloud infrastructure with NVIDIA GPU computing, artificial intelligence, and enterprise-grade security. Accelerate your digital transformation with our cutting-edge cloud solutions.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
          <a routerLink="/contact" class="inline-flex justify-center items-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 shadow-lg hover:shadow-blue-500/30 transform hover:-translate-y-1">
            Get Started
            <i class="bx bx-rocket ml-2 text-xl"></i>
          </a>
          <a routerLink="/gpu-cloud" class="inline-flex justify-center items-center px-8 py-4 text-base font-bold text-white transition-all duration-200 bg-transparent border border-slate-600 rounded-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-600">
            Explore GPU Cloud
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 border-t border-slate-800">
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
              <i class="bx bx-shield-check text-2xl"></i>
            </div>
            <div>
              <div class="text-2xl font-bold text-white">99.99%</div>
              <div class="text-xs text-slate-400 uppercase tracking-wide">Uptime SLA</div>
            </div>
          </div>
          
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-400">
              <i class="bx bx-time text-2xl"></i>
            </div>
            <div>
              <div class="text-2xl font-bold text-white">30s</div>
              <div class="text-xs text-slate-400 uppercase tracking-wide">Deploy Time</div>
            </div>
          </div>
          
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400">
              <i class="bx bx-support text-2xl"></i>
            </div>
            <div>
              <div class="text-2xl font-bold text-white">24/7</div>
              <div class="text-xs text-slate-400 uppercase tracking-wide">AI Support</div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="relative lg:ml-10">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-slate-700/50 bg-slate-800/50 backdrop-blur-sm">
          <img src="assets/img/windows-cloud-hero.png" alt="AI-Powered Cloud Infrastructure" class="w-full h-auto">
        </div>
        
        <!-- Floating Badge 1 -->
        <div *ngIf="showGpuTiles" class="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-xl animate-float-slow z-20 max-w-[180px]">
          <div class="flex items-center gap-3 mb-2">
            <div class="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
              <i class="bx bx-chip text-lg"></i>
            </div>
            <span class="text-sm font-bold text-slate-800">550+ GPUs</span>
          </div>
          <div class="text-xs text-slate-500">High Performance</div>
        </div>
        
        <!-- Floating Badge 2 -->
        <div class="absolute -bottom-6 -left-6 bg-slate-800 p-4 rounded-xl shadow-xl border border-slate-700 animate-float-delayed z-20">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white">
              <i class="bx bx-rocket text-xl"></i>
            </div>
            <div>
              <div class="text-sm font-bold text-white">16+ Years</div>
              <div class="text-xs text-slate-400">Experience</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- AI & GPU Features Section -->
<section class="py-24 bg-white">
  <div class="container mx-auto px-4">
    <div class="text-center mb-20">
      <div class="inline-block bg-blue-50 text-blue-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
        AI & GPU Cloud Solutions
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
        Accelerate Your <span class="text-blue-600">AI Workloads</span>
      </h2>
      <p class="text-xl text-slate-600 max-w-3xl mx-auto">
        Leverage cutting-edge NVIDIA GPU technology and AI-optimized infrastructure for machine learning, deep learning, and high-performance computing.
      </p>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- NVIDIA H100 Tensor Core GPUs -->
      <div class="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2 group">
        <div class="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md">
          <i class="bx bx-chip text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-slate-900 mb-3">NVIDIA H100 Tensor Core GPUs</h3>
        <p class="text-slate-600 mb-6">
          Experience unprecedented AI performance with the latest NVIDIA H100 GPUs, delivering up to 3x faster training and 30x faster inference.
        </p>
        <ul class="space-y-3 mb-6">
          <li class="flex items-center text-slate-700 text-sm">
            <i class="bx bx-check-circle text-yellow-500 mr-3 text-lg"></i>
            <span>80GB HBM3 Memory</span>
          </li>
          <li class="flex items-center text-slate-700 text-sm">
            <i class="bx bx-check-circle text-yellow-500 mr-3 text-lg"></i>
            <span>Transformer Engine</span>
          </li>
          <li class="flex items-center text-slate-700 text-sm">
            <i class="bx bx-check-circle text-yellow-500 mr-3 text-lg"></i>
            <span>FP8 Support</span>
          </li>
        </ul>
      </div>
      
      <!-- AI/ML Platform -->
      <div class="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2 group">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md">
          <i class="bx bx-brain text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-slate-900 mb-3">AI/ML Platform</h3>
        <p class="text-slate-600 mb-6">
          End-to-end machine learning platform with pre-configured frameworks, optimized libraries, and seamless scalability for your AI projects.
        </p>
        <ul class="space-y-3 mb-6">
          <li class="flex items-center text-slate-700 text-sm">
            <i class="bx bx-check-circle text-blue-500 mr-3 text-lg"></i>
            <span>TensorFlow & PyTorch</span>
          </li>
          <li class="flex items-center text-slate-700 text-sm">
            <i class="bx bx-check-circle text-blue-500 mr-3 text-lg"></i>
            <span>Jupyter Notebooks</span>
          </li>
          <li class="flex items-center text-slate-700 text-sm">
            <i class="bx bx-check-circle text-blue-500 mr-3 text-lg"></i>
            <span>AutoML Tools</span>
          </li>
        </ul>
      </div>
      
      <!-- High-Performance Computing -->
      <div class="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2 group">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md">
          <i class="bx bx-server text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-slate-900 mb-3">High-Performance Computing</h3>
        <p class="text-slate-600 mb-6">
          Optimized compute clusters for scientific simulations, financial modeling, and computational research with dedicated GPU resources.
        </p>
        <ul class="space-y-3 mb-6">
          <li class="flex items-center text-slate-700 text-sm">
            <i class="bx bx-check-circle text-purple-500 mr-3 text-lg"></i>
            <span>InfiniBand Networking</span>
          </li>
          <li class="flex items-center text-slate-700 text-sm">
            <i class="bx bx-check-circle text-purple-500 mr-3 text-lg"></i>
            <span>SLURM Workload Manager</span>
          </li>
          <li class="flex items-center text-slate-700 text-sm">
            <i class="bx bx-check-circle text-purple-500 mr-3 text-lg"></i>
            <span>Container Orchestration</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</section>

<section class="py-24 bg-gray-50">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-blue-50 text-blue-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
        Our Core Expertise
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
        Comprehensive <span class="text-blue-600">Cloud Services</span>
      </h2>
      <p class="text-xl text-slate-600 max-w-3xl mx-auto">
        From DevOps automation to enterprise cloud infrastructure, we deliver end-to-end solutions to power your digital transformation.
      </p>
    </div>

    <div class="technology-wrap owl-carousel owl-theme">
      <div class="item" *ngFor="let service of services" style="padding: 18px;">
        <div class="relative">
          <div
            class="h-full bg-white rounded-2xl shadow-2xl p-8 border transition-all duration-300 hover:shadow-3xl overflow-hidden group"
            [ngClass]="{
              'border-blue-500 ring-4 ring-blue-50/50 transform scale-[1.02] z-10': service.highlight,
              'border-gray-100 hover:-translate-y-2': !service.highlight
            }"
          >
            <div class="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl opacity-30" [ngClass]="['bg-gradient-to-br', service.gradient]"></div>
            <div class="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-3xl opacity-20" [ngClass]="['bg-gradient-to-br', service.gradient]"></div>

            <div class="flex items-start justify-between mb-6">
              <div class="w-16 h-16 rounded-2xl flex items-center justify-center shadow-md" [ngClass]="['bg-gradient-to-br', service.gradient, service.highlight ? 'text-white' : 'text-white']">
                <i [class]="service.icon + ' text-3xl'"></i>
              </div>
              <span
                *ngIf="service.highlight"
                class="text-xs font-bold px-3 py-1 rounded-full shadow-sm"
                [ngClass]="['bg-gradient-to-l from-blue-600 to-blue-500 text-white']"
              >
                CORE SERVICE
              </span>
            </div>

            <h3 class="text-2xl font-extrabold text-slate-900 mb-3 tracking-tight">{{ service.title }}</h3>
            <p class="text-slate-600 mb-8 leading-relaxed">{{ service.description }}</p>

            <div class="flex items-center justify-between">
              <a
                [routerLink]="service.link"
                class="inline-flex items-center font-bold transition-all duration-200 px-5 py-2.5 rounded-xl shadow-md"
                [ngClass]="['bg-gradient-to-r', service.gradient, 'text-white hover:opacity-90']"
              >
                <span>Learn More</span>
                <i class="bx bx-right-arrow-alt ml-2 text-xl transition-transform group-hover:translate-x-1"></i>
              </a>
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full" [ngClass]="['bg-gradient-to-br', service.gradient]"></span>
                <span class="w-2 h-2 rounded-full bg-slate-200"></span>
                <span class="w-2 h-2 rounded-full bg-slate-300"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-24">
      <div class="text-center mb-12">
        <div class="inline-block bg-indigo-50 text-indigo-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">All Services</div>
        <h3 class="text-3xl md:text-4xl font-bold text-slate-900">Explore Our Complete Portfolio</h3>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div *ngFor="let service of services" class="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden transition-all hover:-translate-y-2">
          <div class="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-25" [ngClass]="['bg-gradient-to-br', service.gradient]"></div>
          <div class="p-8">
            <div class="flex items-start justify-between mb-6">
              <div class="w-14 h-14 rounded-xl flex items-center justify-center shadow-md" [ngClass]="['bg-gradient-to-br', service.gradient, 'text-white']">
                <i [class]="service.icon + ' text-2xl'"></i>
              </div>
              <span *ngIf="service.highlight" class="text-xs font-bold px-3 py-1 rounded-full shadow-sm" [ngClass]="['bg-gradient-to-l from-blue-600 to-blue-500 text-white']">CORE</span>
            </div>
            <h4 class="text-xl font-extrabold text-slate-900 mb-2">{{ service.title }}</h4>
            <p class="text-slate-600 mb-5">{{ service.description }}</p>
            <ul class="space-y-2 mb-6">
              <li *ngFor="let f of service.features" class="flex items-center text-slate-700 text-sm">
                <i class="bx bx-check-circle mr-2" [ngClass]="service.accent"></i>
                <span>{{ f }}</span>
              </li>
            </ul>
            <a [routerLink]="service.link" class="inline-flex items-center font-bold transition-all duration-200 px-5 py-2.5 rounded-xl shadow-md" [ngClass]="['bg-gradient-to-r', service.gradient, 'text-white hover:opacity-90']">
              <span>Learn More</span>
              <i class="bx bx-right-arrow-alt ml-2 text-xl"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="py-24 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
  <div class="container mx-auto px-4">
    <div class="max-w-4xl mx-auto text-center">
      <h2 class="text-4xl md:text-5xl font-bold text-white mb-6">
        Ready to Accelerate Your <span class="text-blue-200">AI Journey</span>?
      </h2>
      <p class="text-xl text-blue-100 mb-12 max-w-2xl mx-auto">
        Join thousands of businesses leveraging our AI-powered cloud infrastructure to drive innovation and growth.
      </p>
      
      <div class="flex flex-col sm:flex-row gap-6 justify-center mb-12">
        <a routerLink="/contact" class="bg-white text-blue-700 font-bold py-4 px-10 rounded-xl transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl flex items-center justify-center group">
          <span>Get Started Today</span>
          <i class="bx bx-right-arrow-alt ml-2 text-xl group-hover:translate-x-1 transition-transform"></i>
        </a>
        <a routerLink="/pricing" class="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold py-4 px-10 rounded-xl transition-all duration-300 flex items-center justify-center group">
          <span>View Pricing</span>
          <i class="bx bx-purchase-tag ml-2 text-xl"></i>
        </a>
      </div>
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <div class="text-3xl font-bold text-white mb-2">16+</div>
          <div class="text-sm text-blue-100">Years Experience</div>
        </div>
        <div *ngIf="showGpuTiles" class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <div class="text-3xl font-bold text-white mb-2">550+</div>
          <div class="text-sm text-blue-100">GPU Servers</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <div class="text-3xl font-bold text-white mb-2">99.99%</div>
          <div class="text-sm text-blue-100">Uptime SLA</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <div class="text-3xl font-bold text-white mb-2">24/7</div>
          <div class="text-sm text-blue-100">AI Support</div>
        </div>
      </div>
    </div>
  </div>
</section>
